import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';

import { useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import {
  GUIDE_PREVIEWS_COLLECTION,
  GUIDE_PREVIEWS_DOC_ID,
  type GuidePreviewsDoc,
} from '../schemas/guidePreviews';

/**
 * Reads the materialized `theaWebGuidePreviews/current` doc — ONE Firestore
 * read for every peek strip on the /gift-guides browse tab — and returns its
 * `previews` map (listing key → final image urls, resolved and compacted by
 * the hub writer; see guidePreviews.ts).
 *
 * This replaces the per-product hydration fan-out (~4 reads × ~47 guides per
 * view): the browse tab now follows the same materialize-then-read-once
 * pattern as every other product surface (carouselSessions, the similar
 * rail). Fetch-on-open, not streamed — a daily-rebuilt doc doesn't need a
 * listener.
 *
 * Failure/absence never crashes the tab: the map stays empty and the strips
 * keep their placeholder tiles (GuideBrowse pads short/missing entries).
 *
 * `skip` suppresses the read entirely (returns the empty map) for callers that
 * already hold their images — stories and tests injecting fixtures. Hooks
 * can't be called conditionally, so the opt-out is a parameter rather than an
 * `if` at the call site; without it a story would bind this read against
 * Storybook's stub db.
 */
export function useGuidePreviewDoc(skip = false): Record<string, string[]> {
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [previews, setPreviews] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (skip) return;
    let cancelled = false;

    (async () => {
      try {
        await ensureAuth();
        const snap = await getDoc(
          doc(db, GUIDE_PREVIEWS_COLLECTION, GUIDE_PREVIEWS_DOC_ID),
        );
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as Partial<GuidePreviewsDoc>;
        if (data.previews && typeof data.previews === 'object') {
          setPreviews(data.previews as Record<string, string[]>);
        }
      } catch (err) {
        // Leave the strips on their placeholder tiles — never crash the tab
        // over preview art.
        // eslint-disable-next-line no-console
        console.warn('[gift-guides browse] preview doc read failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, ensureAuth, skip]);

  return previews;
}

export default useGuidePreviewDoc;
